import React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import {
  ProcurementValueObjectsPurchaseOrderStatusEnum,
  PurchaseOrdersDataCollectionDocument,
  UpdatePurchaseOrderStatusDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import type { PurchaseOrderDetailFieldsFragment } from 'generated/resources/graphql'
import { UpdatePurchaseOrderModal } from 'modules/procurement/components/purchaseOrders/UpdateOrderModal'

interface Props {
  purchaseOrder: PurchaseOrderDetailFieldsFragment
}

const Footer = ({ purchaseOrder }: Props) => {
  const { company } = useCurrentSession()

  const actionsForCurrentStatus: Record<
    ProcurementValueObjectsPurchaseOrderStatusEnum,
    {
      nextStatus: ProcurementValueObjectsPurchaseOrderStatusEnum
      label: string
      iconName: React.ComponentProps<typeof Button.Split>['iconName'] | undefined
      message: string
    }
  > = {
    ['processing']: {
      nextStatus: 'processing',
      label: i18n.t('procurement.purchase_orders.status.processing'),
      iconName: undefined,
      message: '',
    },
    ['pending']: {
      nextStatus: 'ordered',
      label: i18n.t('procurement.purchase_orders.actions.mark_ordered'),
      iconName: 'check',
      message: i18n.t('procurement.purchase_orders.responses.ordered.success'),
    },
    ['ordered']: {
      nextStatus: 'received',
      label: i18n.t('procurement.purchase_orders.actions.mark_received'),
      iconName: 'check-multi',
      message: i18n.t('procurement.purchase_orders.responses.received.success'),
    },
    ['partial']: {
      nextStatus: 'received',
      label: i18n.t('procurement.purchase_orders.actions.mark_received'),
      iconName: 'check-multi',
      message: i18n.t('procurement.purchase_orders.responses.received.success'),
    },
    ['received']: {
      nextStatus: 'closed',
      label: i18n.t('procurement.purchase_orders.actions.close'),
      iconName: 'check-circle',
      message: i18n.t('procurement.purchase_orders.responses.closed.success'),
    },
    ['closed']: {
      nextStatus: 'pending',
      label: i18n.t('procurement.purchase_orders.actions.reopen'),
      iconName: 'check',
      message: i18n.t('procurement.purchase_orders.responses.reopened.success'),
    },
    ['draft']: {
      nextStatus: 'pending',
      label: i18n.t('procurement.purchase_orders.actions.review'),
      iconName: undefined,
      message: i18n.t('procurement.purchase_orders.responses.reviewed.success'),
    },
  }

  const { label, iconName, nextStatus, message } = actionsForCurrentStatus[purchaseOrder.status]

  const [updatingStatus, setUpdatingStatus] = React.useState(false)

  const [isOpen, setOpen] = React.useState<boolean>(false)

  const [updatePurchaseOrderStatus] = useMutation(UpdatePurchaseOrderStatusDocument, {
    extractorFn: (data) => data.procurementMutations.updateStatusProcurementPurchaseOrder,
  })

  if (purchaseOrder.status === 'processing') {
    return null
  }

  const openModal = () => {
    setOpen(true)
  }

  const closeModal = () => {
    setOpen(false)
  }

  const handleUpdateStatus = async (
    updatedStatus: ProcurementValueObjectsPurchaseOrderStatusEnum,
    overrideMessage?: string
  ) => {
    setUpdatingStatus(true)

    const result = await updatePurchaseOrderStatus({
      variables: {
        companyId: company.id,
        id: purchaseOrder.id,
        status: updatedStatus,
      },
      refetchQueries: [PurchaseOrdersDataCollectionDocument],
    })
    setUpdatingStatus(false)

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: overrideMessage || message,
      })
    }
  }

  if (purchaseOrder.status == 'received') {
    return (
      <Button
        type="positive"
        iconName={iconName}
        onClick={() => handleUpdateStatus(nextStatus)}
        loading={updatingStatus}
      >
        {label}
      </Button>
    )
  }

  if (purchaseOrder.status == 'closed') {
    return (
      <Button
        type="secondary"
        onClick={() => handleUpdateStatus('pending')}
        loading={updatingStatus}
      >
        {label}
      </Button>
    )
  }

  if (purchaseOrder.status == 'draft') {
    return (
      <>
        <Button onClick={openModal} type="positive">
          {label}
        </Button>
        {isOpen && (
          <UpdatePurchaseOrderModal purchaseOrderId={purchaseOrder.id} handleClose={closeModal} />
        )}
      </>
    )
  }

  return (
    <Button.Split
      label={label}
      type="positive"
      iconName={iconName}
      onClick={() => handleUpdateStatus(nextStatus)}
      loading={updatingStatus}
    >
      <Button.Split.Option
        onClick={() =>
          handleUpdateStatus(
            'closed',
            i18n.t('procurement.purchase_orders.responses.closed.success')
          )
        }
        iconName="check-circle"
      >
        {i18n.t('procurement.purchase_orders.actions.close')}
      </Button.Split.Option>
      {purchaseOrder.status === 'ordered' && (
        <Button.Split.Option
          onClick={() =>
            handleUpdateStatus(
              'partial',
              i18n.t('procurement.purchase_orders.responses.received_partial.success')
            )
          }
          iconName="check"
        >
          {i18n.t('procurement.purchase_orders.actions.mark_received_partial')}
        </Button.Split.Option>
      )}
    </Button.Split>
  )
}

export default Footer
